import { Tool } from '../tools/Tool';
import { type EnhancedToolResult, type ToolDefinition, type ToolExecutionContext } from '../types';

import { applyEdits, type CompositionEdit } from './applyEdits';
import { applyHtmlAndLint, resolveActiveHtmlAsset } from './htmlAssetToolHelpers';
import { WriteHtmlAssetTool } from './WriteHtmlAssetTool';

export interface EditHtmlAssetArgs {
    edits: CompositionEdit[];
}

/**
 * Makes surgical find-and-replace edits to the active HtmlAsset's composition instead of
 * re-authoring the whole document. Reads the current HTML from the asset, applies the edits
 * atomically (all match or nothing commits), then writes and re-lints via the shared helper. The
 * find/replace engine (whitespace/`<br>`-tolerant matching, superseded-edit detection, divergence
 * hints on a miss) lives in applyEdits.
 */
export class EditHtmlAssetTool extends Tool<EditHtmlAssetArgs> {
    public static readonly toolName = 'edit_html_asset';

    public override getToolDefinition(): ToolDefinition {
        return {
            type: 'function',
            function: {
                name: EditHtmlAssetTool.toolName,
                description:
                    'Make surgical edits to the current composition by find-and-replace. ' +
                    'Default to this for any change to an existing design — it costs far fewer ' +
                    `tokens than ${WriteHtmlAssetTool.toolName} and is less likely to drop unrelated parts of ` +
                    "the document. Each edit's `old` must match exactly once in the current " +
                    'composition; if it matches zero or multiple times, the call fails and no ' +
                    'change is committed. When a call fails, the whole batch is validated first, ' +
                    'so EVERY failed edit is reported at once and the composition is left ' +
                    'UNCHANGED — fix every reported edit and resend; never build a follow-up batch ' +
                    "assuming part of a rejected call applied. Edits apply in order, so a later edit's " +
                    '`old` can target text introduced by an earlier edit. Use ONE edit per region — ' +
                    'a later edit whose `old` was already changed by an earlier one is reported as ' +
                    'superseded and skipped, not applied. Whitespace and `<br>` breaks are tolerated, ' +
                    'but other text, tags, and entities must match exactly (the current composition ' +
                    'HTML is provided to you at the start of each turn). ' +
                    `Returns lint findings; if any are errors, fix them and call ${EditHtmlAssetTool.toolName} ` +
                    'again before replying to the user.',
                parameters: {
                    type: 'object',
                    properties: {
                        edits: {
                            type: 'array',
                            minItems: 1,
                            description:
                                'Sequential list of edits to apply. Each edit replaces the ' +
                                'first occurrence of `old` with `new`.',
                            items: {
                                type: 'object',
                                properties: {
                                    old: {
                                        type: 'string',
                                        description:
                                            'Substring to find in the current composition. ' +
                                            'Must match exactly once. Include enough surrounding ' +
                                            'context (attributes, neighboring tags) to be unique.',
                                    },
                                    new: {
                                        type: 'string',
                                        description: 'Replacement text. May be empty to delete.',
                                    },
                                },
                                required: ['old', 'new'],
                            },
                        },
                    },
                    required: ['edits'],
                },
            },
        };
    }

    public getToolDisplayName(): string {
        return 'Edit design';
    }

    public get statusIcon(): string {
        return 'code';
    }

    public async execute(
        args: EditHtmlAssetArgs,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const resolved = resolveActiveHtmlAsset(
            this.getState(),
            this.luminary,
            context.conversationId,
        );
        if ('error' in resolved) {
            return resolved.error;
        }
        const { asset, scene } = resolved;

        const { html, skipped, superseded, errors } = applyEdits(asset.html, args.edits);

        if (errors.length > 0) {
            // The whole batch is validated before anything commits, so EVERY failed edit is reported
            // here — fix them all in one resend rather than discovering them one rejected call at a
            // time. The leading line is emphatic that nothing changed: the common failure mode is the
            // agent building its next batch on the assumption that some of a rejected batch's edits
            // had landed, then editing text that only ever existed in the rejected call.
            const failed = new Set(errors.map((e) => e.index));
            const matchedIndices = args.edits
                .map((_, i) => i)
                .filter((i) => !failed.has(i) && !skipped.includes(i) && !superseded.includes(i));
            const lines = [
                `Failed to apply ${errors.length} of ${args.edits.length} edit(s). NOTHING was ` +
                    `committed — ${EditHtmlAssetTool.toolName} is atomic, so the composition is UNCHANGED from ` +
                    'before this call. Do not assume any edit in this batch (even the ones that ' +
                    'matched) landed. Fix every failed edit below and resend.',
            ];
            for (const e of errors) {
                lines.push(
                    e.context
                        ? `\n• edit #${e.index + 1}: ${e.reason}\n${e.context}`
                        : `\n• edit #${e.index + 1}: ${e.reason}`,
                );
            }
            if (matchedIndices.length > 0) {
                lines.push(
                    `\nEdits #${matchedIndices
                        .map((i) => i + 1)
                        .join(
                            ', #',
                        )} matched the composition as it is now, but fixing the failed edits above can shift the surrounding text — re-check them against the current composition before you resend.`,
                );
            }
            return { result: lines.join('\n'), isSuccess: false };
        }

        // Every edit was a no-op: nothing changed, so don't re-lint/write.
        if (skipped.length === args.edits.length) {
            return {
                result: `No changes applied — all ${args.edits.length} edit(s) were no-ops (old === new).`,
                isSuccess: false,
            };
        }

        const outcome = await applyHtmlAndLint(
            this.luminary,
            asset,
            html,
            scene.duration,
            context.conversationId,
        );
        const notes: string[] = [];
        if (superseded.length > 0) {
            notes.push(
                `Note: skipped ${superseded.length} superseded edit(s) — their \`old\` was already ` +
                    `changed by an earlier edit in this batch, so they were redundant: #${superseded
                        .map((i) => i + 1)
                        .join(', #')}.`,
            );
        }
        if (skipped.length > 0) {
            notes.push(
                `Note: skipped ${skipped.length} no-op edit(s) (old === new): #${skipped.map((i) => i + 1).join(', #')}.`,
            );
        }
        if (notes.length > 0) {
            return { ...outcome, result: `${notes.join('\n')}\n${outcome.result}` };
        }
        return outcome;
    }
}
